/* eslint-disable no-lone-blocks */
import {
  useContext,
  type MutableRefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import { z } from 'zod';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { UserContext } from '../../contexts';

import {
  LoanStatus,
  Modal,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionSubTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import { SimpleTable, SimpleTableRow } from '../molecules';
import { type TLoan, type TLoanDetail } from '../../common';
import {
  FormattedCurrency,
  FormattedDate,
  FormattedMobile
} from '../../common/utils/formatters';

import { APIEndPoints, type AdditionalHeaders, Request } from '../../apis';

type TLoanModal = {
  modalTitle?: string;
  content: TLoan;
  isModalOpen: boolean;
  handleModalClose: () => void;
};

type TLoanModalDetail = TLoanDetail | null | false;

type TProps = {
  pdfBlob: string;
  fileName: string;
  pdfRef: MutableRefObject<HTMLAnchorElement>;
};

const FakeLink = styled.a`
  display: none;
  visibility: hidden;
  opacity: 0;
`;

const LoanModal = ({
  modalTitle,
  content,
  isModalOpen,
  handleModalClose
}: TLoanModal) => {
  const {
    originalAccountNumber,
    applicationId,
    mambuAccountId,
    creationDate,
    borrower,
    student,
    planAmount,
    mambuAccountState,
    repaymentFrequency,
    disbursementDate
  } = content;

  const { userInfo } = useContext(UserContext);

  const [loanDetails, setLoanDetails] = useState<TLoanModalDetail>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const [downloadPdfLink, setDownloadPdfLink] =
    useState<string>('DEFAULT_LINK');
  const [downloadPdfFileName, setDownloadPdfFileName] =
    useState<string>('DEFAULT.pdf');

  useEffect(() => {
    if (
      isModalOpen === true &&
      (loanDetails === null || loanDetails === false)
    ) {
      const getLoans = async () => {
        const loanDetailsSchema = z.object({
          email: z.string().nullable(),
          phoneNumber: z.string().nullable(),
          postcode: z.string().nullable(),
          salesPerson: z.string().nullable(),
          firstRepaymentDate: z.string(),
          nextRepaymentDate: z.string().nullable(),
          disbursedAmount: z.number().nullable(),
          loanTermInMonths: z.string(),
          pasDowngrade: z.boolean().nullable(),
          totalPaid: z.number(),
          planBalance: z.number(),
          repaymentAmount: z.number().nullable(),
          totalDue: z.number(),
          daysInArrears: z.number(),
          contractPdfId: z.string().optional().nullable(),
          nimbleID: z.string().optional().nullable(),
          msfAmount: z.string().optional().nullable(),
          msfGstAmount: z.string().optional().nullable(),
          balanceToEP: z.string().optional().nullable(),
          establishmentFee: z.string().optional().nullable(),
          salesPersonId: z.string().optional().nullable(),
          merchantContactEmail: z.string().optional().nullable()
        });

        //#TODO rework width access key --------------------------
        const details = await Request.safeGet({
          path: `${APIEndPoints.mambuLoan}/${mambuAccountId}`,
          schema: loanDetailsSchema
        });

        if (details.type === 'right') {
          setLoanDetails(details.value);
        } else {
          setLoanDetails(false);
        }
      };

      getLoans();
    }
  }, [isModalOpen]);

  const isPdfDownloadEnabled = (
    loanDetails: TLoanModalDetail
  ): loanDetails is TLoanDetail => {
    if (!loanDetails) {
      return false;
    }
    if (!loanDetails.nimbleID || !loanDetails.contractPdfId) {
      return false;
    }
    return true;
  };

  const getHeaders = (
    loanApplicationSalesAgentUserId?: string | null,
    loanApplicationSalesAgentEmail?: string | null
  ): AdditionalHeaders | 'NO_HEADERS' => {
    if (!loanApplicationSalesAgentUserId && !loanApplicationSalesAgentEmail) {
      return 'NO_HEADERS';
    }
    if (loanApplicationSalesAgentUserId && loanApplicationSalesAgentEmail) {
      return {
        loanApplicationSalesAgentUserId,
        loanApplicationSalesAgentEmail
      };
    }
    if (loanApplicationSalesAgentUserId) {
      return {
        loanApplicationSalesAgentUserId
      };
    }
    if (loanApplicationSalesAgentEmail) {
      return {
        loanApplicationSalesAgentEmail
      };
    }
    // Should never get here but gotta make the linter happy
    return 'NO_HEADERS';
  };
  const downloadPdf = async (
    nimbleID: string,
    contractPdfId: string,
    loanApplicationSalesAgentUserId?: string | null,
    loanApplicationSalesAgentEmail?: string | null
  ) => {
    const maybeHeaders = getHeaders(
      loanApplicationSalesAgentUserId,
      loanApplicationSalesAgentEmail
    );
    const headers = maybeHeaders === 'NO_HEADERS' ? undefined : maybeHeaders;

    const pdfReponse = await Request.getPdf(
      `${APIEndPoints.contracts}/pdf/${nimbleID}/${contractPdfId}`,
      headers
    );

    if (pdfReponse) {
      const data = [];
      data.push(pdfReponse.data);
      const blob: Blob = new Blob(data, {
        type: 'application/pdf'
      });
      const newTabUrl = window.URL.createObjectURL(blob);
      return newTabUrl;
    } else {
      // console.log('error downloading pdf');
      return null;
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    if (
      isPdfDownloadEnabled(loanDetails) &&
      loanDetails.nimbleID &&
      loanDetails.contractPdfId
    ) {
      const pdf = await downloadPdf(
        loanDetails.nimbleID,
        loanDetails.contractPdfId,
        loanDetails.salesPersonId,
        loanDetails.merchantContactEmail
      );

      if (pdf) {
        setDownloadPdfLink(pdf);
        const contractDate = loanDetails.contractPdfId.split(
          'Nimble-Anytime-Contract-'
        )[1];
        const fileName = `PaymentAssistAgreement-${contractDate}`;
        setDownloadPdfFileName(fileName);
        setDownloadingPdf(false);
      } else {
        setDownloadingPdf(false);
        toast.error('There was a problem downloading the contract');
      }
    } else {
      setDownloadingPdf(false);
      toast.error('Missing details, unable to download the contract');
    }
  };

  const pdfRef = useRef<HTMLAnchorElement>(null);
  const isPAS =
    modalTitle === 'Payment Assist' || modalTitle === 'Deferred Payment Assist';

  useEffect(() => {
    if (downloadPdfLink) {
      pdfRef.current?.click();
    }
  }, [downloadPdfLink]);

  return (
    <Modal
      handleClose={handleModalClose}
      isModalVisible={isModalOpen}
      content={
        <>
          {modalTitle && <SectionPreTitle>{modalTitle}</SectionPreTitle>}
          <SectionTitle>{originalAccountNumber}</SectionTitle>
          <SectionSubTitle>
            Created:&nbsp;
            <FormattedDate date={creationDate} />
          </SectionSubTitle>
          <div>
            <SectionWrapper>
              <SectionHeading>Basic Details</SectionHeading>
              <SimpleTable>
                <SimpleTableRow
                  title="Borrower Name"
                  contentType="text"
                  content={borrower || ''}
                />
                <SimpleTableRow
                  title="Student Name"
                  contentType="text"
                  content={student || ''}
                />
                <SimpleTableRow
                  title="Email"
                  contentType="text"
                  content={
                    loanDetails && loanDetails.email ? loanDetails.email : ''
                  }
                  loading={loanDetails === null} //added if data not prefilled from listing call
                  breakAll
                />
                <SimpleTableRow
                  title="Mobile Phone"
                  contentType="text"
                  content={
                    loanDetails && loanDetails.phoneNumber ? (
                      <FormattedMobile mobile={loanDetails.phoneNumber} />
                    ) : (
                      ''
                    )
                  }
                  loading={loanDetails === null}
                />

                <SimpleTableRow
                  title="Postal Code"
                  contentType="text"
                  content={
                    loanDetails && loanDetails.postcode
                      ? loanDetails.postcode
                      : ''
                  }
                  loading={loanDetails === null}
                />
              </SimpleTable>
            </SectionWrapper>
            <SectionWrapper>
              <SectionHeading>Details</SectionHeading>
              <SimpleTable>
                {applicationId && applicationId !== 'NO_DATA' && (
                  <SimpleTableRow
                    title="App ID"
                    contentType="text"
                    content={
                      applicationId.includes('-')
                        ? applicationId.split('-')[0]
                        : applicationId
                    }
                  />
                )}

                {loanDetails && loanDetails.salesPerson && (
                  <SimpleTableRow
                    title="Sales Agent"
                    contentType="text"
                    content={loanDetails.salesPerson}
                  />
                )}

                {mambuAccountId && mambuAccountId !== originalAccountNumber && (
                  <SimpleTableRow
                    title="Mambu ID"
                    contentType="text"
                    content={mambuAccountId}
                  />
                )}
                {mambuAccountState && modalTitle !== 'SNPL' && (
                  <SimpleTableRow
                    title="Status"
                    contentType="text"
                    content={
                      <LoanStatus statusText={mambuAccountState} small={true} />
                    }
                  />
                )}
                {loanDetails && loanDetails.pasDowngrade === true && (
                  <SimpleTableRow
                    title="Converted to Payment Assist"
                    contentType="text"
                    content="Yes"
                  />
                )}

                <SimpleTableRow title="" contentType="hr" />

                <SimpleTableRow
                  title="First Payment Date"
                  contentType="text"
                  content={
                    loanDetails && loanDetails.firstRepaymentDate ? (
                      <FormattedDate date={loanDetails.firstRepaymentDate} />
                    ) : (
                      ''
                    )
                  }
                  loading={loanDetails === null}
                />

                {planAmount !== 'NO_DATA' && (
                  <SimpleTableRow
                    title="Amount"
                    contentType="text"
                    content={<FormattedCurrency amount={planAmount} />}
                  />
                )}

                {/* Only display on SNPL page */}
                {modalTitle === 'SNPL' && (
                  <>
                    <SimpleTableRow
                      title="Disburse Amount"
                      contentType="text"
                      content={
                        loanDetails && loanDetails.balanceToEP ? (
                          <FormattedCurrency
                            amount={parseFloat(loanDetails.balanceToEP)}
                          />
                        ) : (
                          ''
                        )
                      }
                      loading={loanDetails === null}
                    />
                    <SimpleTableRow
                      title="Disbursement Date"
                      contentType="text"
                      content={
                        disbursementDate ? (
                          <FormattedDate date={disbursementDate} />
                        ) : (
                          ''
                        )
                      }
                    />
                    <SimpleTableRow
                      title="Merchant Service Fee"
                      contentType="text"
                      content={
                        loanDetails && loanDetails.msfAmount ? (
                          <FormattedCurrency
                            amount={parseFloat(loanDetails.msfAmount)}
                          />
                        ) : (
                          ''
                        )
                      }
                      loading={loanDetails === null}
                    />
                    <SimpleTableRow
                      title="GST"
                      contentType="text"
                      content={
                        loanDetails && loanDetails.msfGstAmount ? (
                          <FormattedCurrency
                            amount={parseFloat(loanDetails.msfGstAmount)}
                          />
                        ) : (
                          ''
                        )
                      }
                      loading={loanDetails === null}
                    />
                    <SimpleTableRow
                      title="Establishment Fee"
                      contentType="text"
                      content={
                        loanDetails && loanDetails.establishmentFee ? (
                          <FormattedCurrency
                            amount={parseFloat(loanDetails.establishmentFee)}
                          />
                        ) : (
                          ''
                        )
                      }
                      loading={loanDetails === null}
                    />
                  </>
                )}

                {/* Only display on PAS page */}
                {isPAS && (
                  <SimpleTableRow
                    title="Payments to date"
                    contentType="text"
                    content={
                      loanDetails ? (
                        <FormattedCurrency amount={loanDetails.totalPaid} />
                      ) : (
                        ''
                      )
                    }
                    loading={loanDetails === null}
                  />
                )}

                {/* Only display on PAS page */}
                {loanDetails && loanDetails.planBalance && isPAS && (
                  <SimpleTableRow
                    title="Plan Balance"
                    contentType="text"
                    content={
                      <FormattedCurrency amount={loanDetails.planBalance} />
                    }
                  />
                )}

                {/* Only display on PAS page */}
                {isPAS && (
                  <SimpleTableRow
                    title="Next Payment Amount"
                    contentType="text"
                    content={
                      loanDetails && loanDetails.repaymentAmount ? (
                        <FormattedCurrency
                          amount={loanDetails.repaymentAmount}
                        />
                      ) : (
                        ''
                      )
                    }
                    loading={loanDetails === null}
                  />
                )}

                {/* Only display on PAS page */}
                {isPAS && (
                  <SimpleTableRow
                    title="Next Payment Date"
                    contentType="text"
                    content={
                      loanDetails && loanDetails.nextRepaymentDate ? (
                        <FormattedDate date={loanDetails.nextRepaymentDate} />
                      ) : (
                        ''
                      )
                    }
                    loading={loanDetails === null}
                  />
                )}

                {/* Only display on PAS page */}
                {isPAS && repaymentFrequency && (
                  <SimpleTableRow
                    title="Frequency"
                    contentType="text"
                    content={repaymentFrequency}
                  />
                )}

                {loanDetails && loanDetails.loanTermInMonths && (
                  <SimpleTableRow
                    title="Loan Term in Months"
                    contentType="text"
                    content={loanDetails.loanTermInMonths}
                    loading={loanDetails === null}
                  />
                )}

                {/* Only display on PAS page */}
                {isPAS && loanDetails && loanDetails.totalDue !== 0 && (
                  <>
                    <SimpleTableRow title="" contentType="hr" />
                    <SimpleTableRow
                      title="Delinquent Amount"
                      contentType="text"
                      content={
                        <FormattedCurrency amount={loanDetails.totalDue} />
                      }
                    />
                  </>
                )}

                {/* Only display on PAS page */}
                {isPAS && loanDetails && loanDetails.daysInArrears !== 0 && (
                  <SimpleTableRow
                    title="Days Past Due"
                    contentType="text"
                    content={loanDetails.daysInArrears}
                  />
                )}

                {/* Only display on PAS page and if PAS permissions to download */}
                {isPAS && userInfo.permissions.paymentAssistAgreement && (
                  <>
                    <SimpleTableRow title="" contentType="hr" />
                    <SimpleTableRow
                      title="Signed Payment Agreement"
                      contentType="text"
                      content={
                        <>
                          <SecondaryButton
                            onClick={handleDownloadPdf}
                            disabled={
                              loanDetails === null ||
                              !isPdfDownloadEnabled(loanDetails)
                            }
                            isLoading={loanDetails === null || downloadingPdf}
                          >
                            Download Agreement
                          </SecondaryButton>
                          {downloadPdfLink && (
                            <FakeLink
                              href={downloadPdfLink}
                              download={`${downloadPdfFileName}.pdf`}
                              ref={pdfRef}
                            ></FakeLink>
                          )}
                        </>
                      }
                    />
                  </>
                )}
              </SimpleTable>
            </SectionWrapper>
          </div>
        </>
      }
    />
  );
};

export { LoanModal };
